import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Text, Toggle, Modal } from "@/components/admin/AdminUI";
import { toast } from "sonner";
import { Plus, Pencil, Archive, ArchiveRestore } from "lucide-react";

function PlaceTag({ on, label, testid }) {
  return (
    <span data-testid={testid} data-on={on} className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${on ? "bg-kys-blush text-kys-accent-dark" : "bg-kys-charcoal/5 text-kys-muted line-through"}`}>
      {label}
    </span>
  );
}

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", order: 0, visible: true, show_in_shop: true, show_in_builder: true });
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/categories").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editId) await api.put(`/admin/categories/${editId}`, form);
      else await api.post("/admin/categories", form);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const archive = async (c) => { await api.patch(`/admin/categories/${c.id}/archive?value=${!c.archived}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-3xl text-kys-ink">Categories</h1><p className="text-sm text-kys-muted">Choose where each category appears: the Ready-to-Ship Shop, Build Your Set, or both.</p></div>
        <button data-testid="new-category-btn" onClick={() => { setForm({ name: "", order: items.length, visible: true, show_in_shop: true, show_in_builder: true }); setEditId(null); setOpen(true); }} className="flex items-center gap-1.5 rounded-full bg-kys-ink px-4 py-2 text-sm font-semibold text-white hover:bg-kys-accent"><Plus className="h-4 w-4" /> New</button>
      </div>
      <div className="mt-6 divide-y divide-kys-blush-border rounded-lg border border-kys-blush-border bg-white">
        {items.map((c) => (
          <div key={c.id} data-testid={`category-row-${c.slug}`} className={`flex items-center justify-between gap-3 p-4 ${c.archived ? "opacity-50" : ""}`}>
            <div className="min-w-0">
              <span className="font-medium text-kys-ink">{c.name}</span><span className="ml-2 text-xs text-kys-muted">order {c.order} {c.visible ? "" : "· hidden everywhere"}</span>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                <PlaceTag on={c.show_in_shop !== false} label="Ready-to-Ship Shop" testid={`cat-shop-tag-${c.slug}`} />
                <PlaceTag on={c.show_in_builder !== false} label="Build Your Set" testid={`cat-builder-tag-${c.slug}`} />
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <button data-testid={`edit-category-${c.slug}`} aria-label="Edit category" onClick={() => { setForm({ ...c, show_in_shop: c.show_in_shop !== false, show_in_builder: c.show_in_builder !== false }); setEditId(c.id); setOpen(true); }} className="text-kys-muted hover:text-kys-accent"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`archive-category-${c.slug}`} aria-label={c.archived ? "Restore category" : "Archive category"} onClick={() => archive(c)} className="text-kys-muted hover:text-kys-accent">{c.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</button>
            </div>
          </div>
        ))}
      </div>
      <Modal open={open} onClose={() => setOpen(false)} title={editId ? "Edit Category" : "New Category"}>
        <div className="space-y-4">
          <Text label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="cf-name" />
          <Text label="Order" type="number" value={form.order} onChange={(v) => setForm({ ...form, order: v })} />
          <Toggle label="Visible (turn off to hide everywhere)" checked={form.visible} onChange={(v) => setForm({ ...form, visible: v })} testid="cf-visible" />
          <Toggle label="Show in Ready-to-Ship Shop" checked={form.show_in_shop} onChange={(v) => setForm({ ...form, show_in_shop: v })} testid="cf-show-in-shop" />
          <Toggle label="Show in Build Your Set" checked={form.show_in_builder} onChange={(v) => setForm({ ...form, show_in_builder: v })} testid="cf-show-in-builder" />
          <button data-testid="save-category" onClick={save} className="w-full rounded-full bg-kys-ink py-3 text-sm font-semibold text-white hover:bg-kys-accent">Save</button>
        </div>
      </Modal>
    </div>
  );
}
